import { isAxiosError, type AxiosError } from 'axios';
import type { ErrorResponse } from '@/shared/types';
import {
  useTtsBlockedDialogStore,
  type TtsBlockedSource,
} from '@/shared/store/tts-blocked-dialog-store';

/**
 * BE 가 TTS 정책 차단 응답으로 사용하는 식별자 (commit e6f72c61 검증).
 *
 * - 1032: TTS_PROHIBITED_NAME_DETECTED — BE 사전 차단 (실존 인물명/직업 키워드 매칭)
 * - 1031: TTS_PROVIDER_BLOCKED — Gemini safety 차단 (BE 가 422 변환 + 자동 환불)
 *
 * 두 케이스 모두 사용자 행동 유도 (텍스트 수정 / 고객센터 문의) 동일 → 통합 모달.
 */
export const TTS_BLOCKED_HTTP_STATUS = 422;
export const TTS_BLOCKED_ERROR_CODES = [1031, 1032] as const;
export type TtsBlockedErrorCode = (typeof TTS_BLOCKED_ERROR_CODES)[number];

interface TtsBlockedErrorData {
  /** BE 가 보내는 enum (politician/celebrity/...). 사용자 화면 미노출. */
  category?: string;
}

interface BlockedErrorBody extends ErrorResponse {
  traceId?: string;
  data?: TtsBlockedErrorData;
}

export interface HandleTtsErrorResult {
  /** dialog 를 띄워 처리 완료한 경우 true. caller 는 추가 toast/분기 skip. */
  handled: boolean;
}

/**
 * AxiosError 가 TTS 정책 차단 응답인지 검사.
 *
 * 매칭 조건: HTTP 422 + body.code === 1031 또는 1032 (Integer).
 */
export function isTtsBlockedError(
  error: unknown,
): error is AxiosError<BlockedErrorBody> {
  if (!isAxiosError(error)) return false;
  if (error.response?.status !== TTS_BLOCKED_HTTP_STATUS) return false;

  const body = error.response.data as BlockedErrorBody | undefined;
  if (!body) return false;

  return (TTS_BLOCKED_ERROR_CODES as readonly number[]).includes(body.code);
}

/**
 * API wrapper (`shared/apis/tts/*`) 가 axios error 를 받았을 때 호출.
 *
 * 1031/1032 매칭 시 store 트리거 + dev 로그. 그 외 에러는 noop.
 *
 * @param error  axios catch 인자 (axios 외 에러 무시)
 * @param source 어느 mutation 에서 차단됐는지 — Amplitude property 용
 */
export function handleTtsError(
  error: unknown,
  source: TtsBlockedSource,
): HandleTtsErrorResult {
  if (!isTtsBlockedError(error)) {
    return { handled: false };
  }

  const body = error.response?.data;
  const traceId = body?.traceId;
  const category = body?.data?.category;

  if (process.env.NEXT_PUBLIC_MODE !== 'production') {
    console.warn('[tts-blocked]', {
      source,
      code: body?.code,
      category,
      traceId,
      message: body?.message,
    });
  }

  useTtsBlockedDialogStore.getState().openBlocked({
    source,
    traceId,
  });

  return { handled: true };
}
